import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { ShoppingCart, ChevronLeft, Minus, Plus, ChevronRight as ChevronR, Clock, X as XIcon, Zap } from "lucide-react";
import ProductReviews from "@/components/ProductReviews";
import { useCategories } from "@/hooks/useCategories";
import { ShowcaseHeroBackground } from "@/components/ShowcaseHeroBackground";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { categories } = useCategories();
  const CATEGORY_LABELS = Object.fromEntries(categories.map(c => [c.key, c.label]));
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addItem, items: cartItems, updateQuantity: updateCartQty, removeItem: removeCartItem } = useCart();
  const { user } = useAuth();

  // Waitlist state
  const [waitlistStatus, setWaitlistStatus] = useState(null);

  // Reviews state
  const [reviewSummary, setReviewSummary] = useState(null);
  const fetchReviews = async () => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/api/products/${id}/reviews`);
      setReviewSummary(data);
    } catch {
      setReviewSummary({ reviews: [], count: 0, average: 0, can_review: false, has_reviewed: false });
    }
  };
  useEffect(() => { fetchReviews();   }, [id, user]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistMessage, setWaitlistMessage] = useState("");
  const [showWaitlistPrompt, setShowWaitlistPrompt] = useState(false);

  // Preorder state
  const [preorderStatus, setPreorderStatus] = useState(null);
  const [preorderLoading, setPreorderLoading] = useState(false);
  const [preorderError, setPreorderError] = useState("");
  const [showPreorderForm, setShowPreorderForm] = useState(false);
  const [preorderQty, setPreorderQty] = useState(1);
  const [preorderShipping, setPreorderShipping] = useState({
    full_name: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "US", phone: "",
  });

  // Color customization
  const [selectedColor, setSelectedColor] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products/${id}`);
        setProduct(data);
      } catch (e) {
        console.error("Failed to fetch product", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // Check waitlist and preorder status when product loaded
  useEffect(() => {
    if (!product || product.stock > 0) return;
    // Check preorder status (public, no auth needed)
    const checkPreorder = async () => {
      try {
        const { data } = await axios.get(`${API}/api/preorder/status/${product.id}`);
        setPreorderStatus(data);
      } catch { /* ignore */ }
    };
    checkPreorder();
    // Check waitlist status (auth needed)
    if (!user) return;
    const checkWaitlist = async () => {
      try {
        const { data } = await axios.get(`${API}/api/waitlist/check/${product.id}`);
        setWaitlistStatus(data);
        if (!data.on_waitlist) {
          setShowWaitlistPrompt(true);
        }
      } catch { /* ignore */ }
    };
    checkWaitlist();
  }, [product, user]);

  const handleJoinWaitlist = async () => {
    if (!user) {
      navigate("/login?redirect=/products/" + id);
      return;
    }
    setWaitlistLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/waitlist/join`, { product_id: product.id });
      setWaitlistMessage(data.message);
      setWaitlistStatus({ on_waitlist: true, entry: { position: data.position, id: data.entry_id } });
      setShowWaitlistPrompt(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setWaitlistMessage(typeof detail === "string" ? detail : "Failed to join waitlist");
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleDeclineWaitlist = () => {
    setShowWaitlistPrompt(false);
    navigate("/");
  };

  const handlePreorder = async () => {
    if (!user) { navigate("/login?redirect=/products/" + id); return; }
    if (!preorderShipping.full_name || !preorderShipping.address_line1 || !preorderShipping.city || !preorderShipping.state || !preorderShipping.zip_code) {
      setPreorderError("Please fill in all required shipping fields.");
      return;
    }
    setPreorderLoading(true);
    setPreorderError("");
    try {
      const { data } = await axios.post(`${API}/api/preorder/create-session`, {
        product_id: product.id,
        origin_url: window.location.origin,
        shipping_address: preorderShipping,
        quantity: preorderQty,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setPreorderError(typeof detail === "string" ? detail : "Failed to start preorder");
    } finally {
      setPreorderLoading(false);
    }
  };

  const images = product?.image_urls?.length ? product.image_urls : product?.image_url ? [product.image_url] : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ backgroundColor: "#050505" }}>
        <p className="text-[#A1A1A1] text-lg">Product not found</p>
        <Link to="/products">
          <Button variant="outline" className="bg-transparent border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] rounded-none font-mono text-xs uppercase">
            Back to Store
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      {/* Top hero band — greyed-out showcase photo backdrop behind the back button. */}
      <div className="relative overflow-hidden border-b border-[#1A1A1A]">
        <ShowcaseHeroBackground intervalMs={9000} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/products"
            data-testid="back-to-products"
            className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1] hover:text-[#39FF14] transition-colors"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            Back to Store
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[1px] bg-[#1A1A1A]">
          {/* Image Gallery */}
          <div className="bg-[#0D0D0D]">
            {images.length > 0 ? (
              <div>
                {/* Main Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={images[selectedImage]}
                    alt={product.name}
                    data-testid="product-image"
                    className="w-full h-full object-cover"
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setSelectedImage(i => (i - 1 + images.length) % images.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#050505]/70 border border-[#1A1A1A] flex items-center justify-center text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] transition-colors"
                        data-testid="prev-image"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setSelectedImage(i => (i + 1) % images.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#050505]/70 border border-[#1A1A1A] flex items-center justify-center text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] transition-colors"
                        data-testid="next-image"
                      >
                        <ChevronR className="h-4 w-4" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-[10px] text-[#A1A1A1] bg-[#050505]/70 px-2 py-1">
                        {selectedImage + 1} / {images.length}
                      </div>
                    </>
                  )}
                </div>
                {/* Thumbnails */}
                {images.length > 1 && (
                  <div className="flex gap-[1px] bg-[#1A1A1A] overflow-x-auto" data-testid="image-thumbnails">
                    {images.map((url, idx) => (
                      <button
                        key={`thumb-${url}-${idx}`}
                        onClick={() => setSelectedImage(idx)}
                        className={`w-16 h-16 flex-shrink-0 overflow-hidden border-2 transition-colors ${
                          idx === selectedImage ? "border-[#39FF14]" : "border-transparent hover:border-[#39FF14]/50"
                        }`}
                        data-testid={`thumbnail-${idx}`}
                      >
                        <img src={url} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full aspect-square flex items-center justify-center bg-[#0D0D0D]">
                <span className="font-mono text-[#1A1A1A] text-9xl">X</span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="bg-[#0D0D0D] p-8 sm:p-12 flex flex-col justify-center">
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-4">
              {CATEGORY_LABELS[product.category] || product.category}
            </p>

            <h1
              data-testid="product-name"
              className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-[#F2F2F2] mb-4"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {product.name}
            </h1>

            <p className="text-[#A1A1A1] text-base mb-8 leading-relaxed">
              {product.description}
            </p>

            <div className="border-t border-[#1A1A1A] pt-6 mb-6">
              <div className="flex items-baseline gap-4 mb-2">
                <span className="font-mono text-3xl font-bold text-[#39FF14]" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                <span className="font-mono text-xs text-[#A1A1A1] uppercase tracking-wider">
                  {product.condition}
                </span>
              </div>
              <p className={`font-mono text-xs uppercase tracking-wider ${product.stock > 0 ? "text-[#39FF14]" : "text-red-500"}`} data-testid="product-stock">
                {product.stock > 0 ? `${product.stock} in stock` : "Out of Stock"}
              </p>
            </div>

            {/* Color Customization */}
            {product.color_customizable && (
              <div className="mb-6" data-testid="color-picker">
                <p className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-3">Choose Your Color</p>
                {(product.category === "consoles" || product.category === "bundles") && (
                  <div className="mb-3">
                    <p className="text-[#A1A1A1] text-xs">Your selected color will customize the Jewel color, the frontplate Eject button light ring, and the Modchip LED color (visible through the left side of the console).</p>
                    <p className="text-yellow-400 text-xs font-bold mt-1">Optional — custom color adds $20.00. Leave unselected for stock colors.</p>
                  </div>
                )}
                {product.category === "other" && (
                  <div className="mb-3">
                    <p className="text-[#A1A1A1] text-xs">Your selected color will be the color of your Xbox Logo accessory.</p>
                    <p className="text-yellow-400 text-xs font-bold mt-1">Color selection is required.</p>
                  </div>
                )}
                <div className="flex gap-2">
                  {[
                    { name: "Red", hex: "#EF4444" },
                    { name: "White", hex: "#F2F2F2" },
                    { name: "Green", hex: "#39FF14" },
                    { name: "Blue", hex: "#3B82F6" },
                    { name: "Yellow", hex: "#EAB308" },
                  ].map(({ name, hex }) => (
                    <button
                      key={name}
                      onClick={() => setSelectedColor(name)}
                      data-testid={`color-${name.toLowerCase()}`}
                      className={`w-10 h-10 border-2 transition-all ${selectedColor === name ? "border-[#F2F2F2] scale-110" : "border-[#1A1A1A] hover:border-[#A1A1A1]"}`}
                      style={{ backgroundColor: hex }}
                      title={name}
                    />
                  ))}
                  {(product.category === "consoles" || product.category === "bundles") && selectedColor && (
                    <button onClick={() => setSelectedColor(null)} className="w-10 h-10 border-2 border-[#1A1A1A] hover:border-[#A1A1A1] flex items-center justify-center text-[#A1A1A1] text-xs font-mono" title="No custom color" data-testid="color-none">X</button>
                  )}
                </div>
                {selectedColor && <p className="font-mono text-xs text-[#F2F2F2] mt-2">Selected: <span className="text-[#39FF14]">{selectedColor}</span>{(product.category === "consoles" || product.category === "bundles") ? " (+$20)" : ""}</p>}
                {!selectedColor && product.category === "other" && <p className="font-mono text-xs text-yellow-400 mt-2">Please select a color</p>}
                {!selectedColor && (product.category === "consoles" || product.category === "bundles") && <p className="font-mono text-xs text-[#A1A1A1] mt-2">No custom color selected — stock colors will be used</p>}
              </div>
            )}

            {product.stock > 0 ? (() => {
              const cartItem = cartItems.find(i => i.product_id === product.id);
              const inCartQty = cartItem ? cartItem.quantity : 0;
              const maxCanAdd = product.stock - inCartQty;

              return inCartQty > 0 ? (
                <div className="space-y-4">
                  {/* Already in cart - show inline quantity control */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border border-[#39FF14]">
                      <button
                        data-testid="cart-qty-decrease"
                        onClick={() => inCartQty <= 1 ? removeCartItem(product.id) : updateCartQty(product.id, inCartQty - 1)}
                        className="px-4 py-3 text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] transition-colors"
                      >
                        <Minus className="h-4 w-4" strokeWidth={2} />
                      </button>
                      <span data-testid="cart-qty-value" className="px-4 py-3 font-mono text-sm text-[#39FF14] font-bold min-w-[48px] text-center border-l border-r border-[#39FF14]">
                        {inCartQty}
                      </span>
                      <button
                        data-testid="cart-qty-increase"
                        onClick={() => { if (inCartQty < product.stock) updateCartQty(product.id, inCartQty + 1); }}
                        disabled={inCartQty >= product.stock}
                        className="px-4 py-3 text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#39FF14]"
                      >
                        <Plus className="h-4 w-4" strokeWidth={2} />
                      </button>
                    </div>
                    <span className="font-mono text-sm text-[#39FF14]">in cart</span>
                  </div>
                  {inCartQty >= product.stock && (
                    <p className="font-mono text-xs text-[#A1A1A1]">Maximum stock reached ({product.stock} available)</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Quantity selector */}
                  <div className="flex items-center border border-[#1A1A1A]">
                    <button
                      data-testid="qty-decrease"
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="px-4 py-3 text-[#A1A1A1] hover:text-[#39FF14] transition-colors"
                    >
                      <Minus className="h-4 w-4" strokeWidth={2} />
                    </button>
                    <span data-testid="qty-value" className="px-4 py-3 font-mono text-sm text-[#F2F2F2] min-w-[48px] text-center border-l border-r border-[#1A1A1A]">
                      {quantity}
                    </span>
                    <button
                      data-testid="qty-increase"
                      onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                      disabled={quantity >= product.stock}
                      className="px-4 py-3 text-[#A1A1A1] hover:text-[#39FF14] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </div>

                  <Button
                    data-testid="add-to-cart-btn"
                    onClick={() => { if (product.color_customizable && product.category === "other" && !selectedColor) return; addItem(product, quantity, selectedColor); }}
                    disabled={product.color_customizable && product.category === "other" && !selectedColor}
                    className="flex-1 bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6 transition-colors duration-200 disabled:opacity-50"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" strokeWidth={2} />
                    Add to Cart — ${((product.price + (selectedColor && ["consoles","bundles"].includes(product.category) ? 20 : 0)) * quantity).toFixed(2)}
                  </Button>
                </div>
              );
            })() : (
              /* Out of stock — always show preorder + waitlist */
              <div className="border border-[#1A1A1A] p-6 space-y-4" data-testid="oos-section">
                {showPreorderForm ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="h-4 w-4 text-[#39FF14]" />
                      <p className="text-[#39FF14] font-mono text-xs uppercase tracking-wider font-bold">Preorder{preorderStatus?.slots_left != null ? ` — ${preorderStatus.slots_left} slot${preorderStatus.slots_left !== 1 ? "s" : ""} left` : ""}</p>
                    </div>
                    <p className="text-[#A1A1A1] text-sm">Fill in your shipping address to complete your preorder via Stripe.</p>
                    {/* Quantity selector */}
                    <div className="flex items-center gap-3">
                      <span className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">Qty</span>
                      <div className="flex items-center border border-[#39FF14]">
                        <button onClick={() => setPreorderQty(q => Math.max(1, q - 1))} className="px-3 py-2 text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] transition-colors"><Minus className="h-3 w-3" strokeWidth={2} /></button>
                        <span className="px-4 py-2 font-mono text-sm text-[#39FF14] font-bold border-l border-r border-[#39FF14] min-w-[40px] text-center">{preorderQty}</span>
                        <button onClick={() => setPreorderQty(q => Math.min(preorderStatus?.slots_left || 10, 10, q + 1))} disabled={preorderQty >= (preorderStatus?.slots_left || 10) || preorderQty >= 10} className="px-3 py-2 text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"><Plus className="h-3 w-3" strokeWidth={2} /></button>
                      </div>
                      <span className="font-mono text-sm text-[#39FF14] font-bold">${(product.price * preorderQty).toFixed(2)} + shipping</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { id: "full_name", label: "Full Name", span2: true },
                        { id: "address_line1", label: "Address Line 1", span2: true },
                        { id: "address_line2", label: "Address Line 2 (Optional)", span2: true },
                        { id: "city", label: "City" },
                        { id: "state", label: "State" },
                        { id: "zip_code", label: "ZIP Code" },
                        { id: "country", label: "Country" },
                        { id: "phone", label: "Phone", span2: true },
                      ].map(({ id: fid, label, span2 }) => (
                        <div key={fid} className={span2 ? "sm:col-span-2" : ""}>
                          <label className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1] block mb-1">{label}</label>
                          <input value={preorderShipping[fid]} onChange={(e) => setPreorderShipping(s => ({ ...s, [fid]: e.target.value }))} className="w-full bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] px-3 py-2 text-sm focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14]" />
                        </div>
                      ))}
                    </div>
                    {preorderError && <p className="text-red-400 text-sm">{preorderError}</p>}
                    <div className="border border-yellow-600/30 bg-yellow-500/5 p-3 mb-2">
                      <p className="text-yellow-400 text-xs font-mono uppercase tracking-wider font-bold mb-1">All preorders are final</p>
                      <p className="text-[#A1A1A1] text-xs">By clicking "Preorder" below, you agree that this purchase is non-refundable. Your product is guaranteed and will ship as soon as it's ready.</p>
                    </div>
                    <div className="flex gap-3">
                      <Button data-testid="preorder-pay-btn" onClick={handlePreorder} disabled={preorderLoading} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4">
                        <Zap className="h-3 w-3 mr-2" />{preorderLoading ? "Processing..." : `Preorder${preorderQty > 1 ? ` (${preorderQty})` : ""} — $${(product.price * preorderQty).toFixed(2)}`}
                      </Button>
                      <Button onClick={() => setShowPreorderForm(false)} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider px-6 py-4">Back</Button>
                    </div>
                  </div>
                ) : preorderStatus?.preorder_available ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="h-4 w-4 text-[#39FF14]" />
                      <p className="text-[#39FF14] font-mono text-xs uppercase tracking-wider font-bold">Preorder Available</p>
                    </div>
                    <p className="text-[#F2F2F2] text-sm mb-2">This item is currently out of stock, but you can <strong className="text-[#39FF14]">preorder</strong> now. Preordering requires a deposit but <strong className="text-[#39FF14]">guarantees</strong> you this product. We'll ship it as soon as it's ready.</p>
                    <p className="text-[#A1A1A1] text-xs mb-4 font-mono">{preorderStatus.slots_left} of {preorderStatus.limit} preorder slot{preorderStatus.slots_left !== 1 ? "s" : ""} remaining</p>
                    <div className="flex flex-col gap-3">
                      <Button data-testid="preorder-btn" onClick={() => user ? setShowPreorderForm(true) : navigate("/login?redirect=/products/" + id)} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4">
                        <Zap className="h-3 w-3 mr-2" />{user ? `Preorder — $${product.price.toFixed(2)}` : "Sign In to Preorder"}
                      </Button>
                      {!waitlistStatus?.on_waitlist ? (
                        <p className="text-[#A1A1A1] text-xs">Or <button onClick={() => user ? handleJoinWaitlist() : navigate("/login?redirect=/products/" + id)} className="text-[#39FF14] hover:underline" data-testid="alt-waitlist-btn">{user ? "join the waitlist" : "sign in to join the waitlist"}</button> to be notified when more are available.</p>
                      ) : (
                        <p className="text-[#39FF14] text-xs font-mono">You're also on the waitlist. We'll email you with updates.</p>
                      )}
                    </div>
                  </div>
                ) : preorderStatus && !preorderStatus.preorder_available ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="h-4 w-4 text-red-400" />
                      <p className="text-red-400 font-mono text-xs uppercase tracking-wider font-bold">Preorder Slots Full</p>
                    </div>
                    <p className="text-[#F2F2F2] text-sm mb-2">All <strong className="text-[#39FF14]">{preorderStatus.limit}</strong> preorder slots are currently filled.</p>
                    {waitlistStatus?.on_waitlist ? (
                      <div>
                        <p className="text-[#39FF14] text-sm font-mono mb-2">You're on the waitlist!</p>
                        <p className="text-[#A1A1A1] text-sm">When new slots open, we'll email you immediately. <strong className="text-[#F2F2F2]">The first {preorderStatus.limit} waitlisters to upgrade to a preorder get the guaranteed product.</strong> Pay close attention to your emails!</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-[#A1A1A1] text-sm mb-3">Join the waitlist and we'll email you when preorder slots open. <strong className="text-[#F2F2F2]">The first {preorderStatus.limit} to preorder get the guaranteed product.</strong></p>
                        <Button data-testid="waitlist-preorder-btn" onClick={() => user ? handleJoinWaitlist() : navigate("/login?redirect=/products/" + id)} disabled={waitlistLoading} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4">
                          <Clock className="h-3 w-3 mr-2" />{waitlistLoading ? "Joining..." : user ? "Join Waitlist" : "Sign In to Join Waitlist"}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-[#F2F2F2] text-sm mb-4">This item is currently out of stock. You can <strong className="text-[#39FF14]">preorder</strong> to guarantee your product, or join the waiting list to be notified when more are available.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button data-testid="preorder-btn-loading" onClick={() => user ? setShowPreorderForm(true) : navigate("/login?redirect=/products/" + id)} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-4">
                        <Zap className="h-3 w-3 mr-2" />{user ? `Preorder — $${product.price.toFixed(2)}` : "Sign In to Preorder"}
                      </Button>
                      {!waitlistStatus?.on_waitlist ? (
                        <Button data-testid="waitlist-alt-btn" onClick={() => user ? handleJoinWaitlist() : navigate("/login?redirect=/products/" + id)} disabled={waitlistLoading} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider px-8 py-4">
                          <Clock className="h-3 w-3 mr-2" />{waitlistLoading ? "Joining..." : user ? "Join Waitlist" : "Sign In for Waitlist"}
                        </Button>
                      ) : (
                        <p className="text-[#39FF14] text-xs font-mono self-center">You're on the waitlist</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <ProductReviews
          productId={id}
          user={user}
          summary={reviewSummary}
          onChange={fetchReviews}
        />
      </div>
    </div>
  );
}
